// Coalesce overlapping / rapid-fire invocations of an async "burst" into a
// single serialized run with one trailing re-run, so a storm of triggers can't
// stack N concurrent bursts and amplify load.
//
// WHY (CYBORG-758, part of CYBORG-755): handleReconnect fires a heavy catch-up
// burst (per-workspace subscribe + unread, panel refetch, activity feed, agent
// timelines, tasks, DM resync) on EVERY reconnect. A reconnect storm (the
// heartbeat-terminate loop) otherwise runs N of those bursts concurrently and
// amplifies load. This wrapper guarantees at most ONE burst runs at a time; any
// triggers that arrive while a burst is running collapse into a SINGLE trailing
// re-run once it finishes, so the final state is still fresh (no catch-up is
// skipped) but the storm settles in ~1-2 bursts instead of N.
//
// The trailing loop re-runs only while a fresh trigger actually arrived
// mid-burst (`pending`), so a lone call runs exactly once. `inFlight`/`pending`
// are always cleared in `finally`, so a thrown burst can't wedge the guard.
export function coalesceBurst(run: () => Promise<void>): () => Promise<void> {
  let inFlight = false;
  let pending = false;
  return async function coalesced(): Promise<void> {
    if (inFlight) {
      // A burst is already running — remember another was requested and let the
      // in-flight run pick it up as one trailing re-run when it settles.
      pending = true;
      return;
    }
    inFlight = true;
    try {
      do {
        pending = false;
        await run();
      } while (pending);
    } finally {
      inFlight = false;
      pending = false;
    }
  };
}
